import { useQuery } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "src/api/firebase";
import type {
    ReportStructure,
    DetailCaseComponentAnalysis,
} from "../pages/ReportPage/types";
// -----------------------------
// Firebase Callable Functions
// -----------------------------
const getOverallReport = httpsCallable(functions, "getOverallReport");
const getDetailReport = httpsCallable(functions, "getDetailReport");
const getTranscriptionAndCaseProblem = httpsCallable(
    functions,
    "getTranscriptionAndCaseProblem"
);

// -----------------------------
// Data Fetching Functions
// -----------------------------
const fetchGeneralReport = async (
    sessionId: string
): Promise<ReportStructure> => {
    const response = await getOverallReport({ sessionId });
    // Assumes the response data is in the shape: { found: boolean; data: ReportStructure }
    const result = response.data as { found: boolean; data: ReportStructure };
    return result.data;
};

const fetchDetailedReport = async (
    sessionId: string
): Promise<DetailCaseComponentAnalysis> => {
    const response = await getDetailReport({ sessionId });
    // Assumes the response data is in the shape: { found: boolean; data: DetailCaseComponentAnalysis }
    const result = response.data as {
        found: boolean;
        data: DetailCaseComponentAnalysis;
    };
    return result.data;
};

const fetchTranscriptionAndCaseProblem = async (
    sessionId: string
): Promise<{ transcription: any; caseProblem: any }> => {
    const response = await getTranscriptionAndCaseProblem({ sessionId });
    // Assumes the response data is in the shape: { found: boolean; data: { transcription: string; caseProblem: string } }
    console.log("response", response);
    const result = response as {
        data: { transcription: any; caseProblem: any; interviewData: any };
    };
    console.log("Transcription and case problem", result.data);
    console.log("Transcription", result.data.transcription);
    let transcriptionStr = "";
    result.data.transcription.completeTranscription.forEach((e: any) => {
        transcriptionStr += e.role + ":" + e.text + " ";
    });
    let caseProblemStr = "";
    caseProblemStr += result.data.caseProblem.caseContext.promptContext + " ";
    caseProblemStr += result.data.caseProblem.caseContext.promptQuestion + " ";
    for (
        let i = 0;
        i < result.data.caseProblem.caseContext.additionalInformation.length;
        i++
    ) {
        caseProblemStr +=
            "information" +
            i +
            ":" +
            result.data.caseProblem.caseContext.additionalInformation[i]
                .content +
            " ";
    }
    for (let i = 0; i < result.data.caseProblem.caseQuestions.length; i++) {
        caseProblemStr +=
            "question" +
            i +
            ":" +
            result.data.caseProblem.caseQuestions[i].question +
            " ";
    }

    console.log("Transcription string", transcriptionStr);
    console.log("Case problem string", caseProblemStr);
    return result.data;
};

// -----------------------------
// Custom React Query Hooks
// -----------------------------
export function useFetchGeneralReport(sessionId: string) {
    const query = useQuery<ReportStructure>({
        queryKey: ["generalReport", sessionId],
        queryFn: () => fetchGeneralReport(sessionId),
        enabled: Boolean(sessionId), // only run if sessionId is provided
    });

    return {
        data: query.data,
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
}

export function useFetchDetailedReport(sessionId: string) {
    const query = useQuery<DetailCaseComponentAnalysis>({
        queryKey: ["detailedReport", sessionId],
        queryFn: () => fetchDetailedReport(sessionId),
        enabled: Boolean(sessionId),
    });

    return {
        data: query.data,
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
}

export function useFetchTranscriptionAndCaseProblem(sessionId: string) {
    const query = useQuery<{ transcription: any; caseProblem: any }>({
        queryKey: ["transcriptionAndCaseProblem", sessionId],
        queryFn: () => fetchTranscriptionAndCaseProblem(sessionId),
        enabled: Boolean(sessionId),
    });

    return {
        data: query.data,
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
}
